if (!customElements.get('aogeo-resource-article')) {
  class AogeoResourceArticle extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.body = this.querySelector('[data-article-body]');
      if (!this.body) return;
      this.headings = Array.from(this.body.querySelectorAll('h2'));
      this.tocLists = Array.from(this.querySelectorAll('[data-toc-list]'));
      this.tocLinks = [];
      this.enhanceTables();
      if (!this.headings.length) return;
      this.buildContents();
      this.onContentsClick = this.handleContentsClick.bind(this);
      this.addEventListener('click', this.onContentsClick);
      this.observeHeadings();
      this.restoreInitialFragment();
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      if (this.initialFragmentFrame != null) {
        this.ownerDocument.defaultView.cancelAnimationFrame(this.initialFragmentFrame);
        this.initialFragmentFrame = null;
      }
      if (this.contentsScrollFrame != null) {
        this.ownerDocument.defaultView.cancelAnimationFrame(this.contentsScrollFrame);
        this.contentsScrollFrame = null;
      }
      if (this.onContentsClick) this.removeEventListener('click', this.onContentsClick);
      this.initialized = false;
    }

    buildContents() {
      this.tocLists.forEach((list) => list.replaceChildren());
      this.headings.forEach((heading, index) => {
        if (!heading.id) {
          const base = `${this.id || 'ResourceArticle'}-Section-${index + 1}`;
          let uniqueId = base;
          let suffix = 1;
          while (this.ownerDocument.getElementById(uniqueId)) {
            uniqueId = `${base}-${suffix++}`;
          }
          heading.id = uniqueId;
        }
        this.tocLists.forEach((list) => {
          const item = this.ownerDocument.createElement('li');
          const link = this.ownerDocument.createElement('a');
          link.href = `#${encodeURIComponent(heading.id)}`;
          link.textContent = heading.textContent.trim();
          link.dataset.tocLink = '';
          link.dataset.headingId = heading.id;
          item.append(link);
          list.append(item);
          this.tocLinks.push(link);
        });
      });
      this.querySelectorAll('[data-article-toc]').forEach((toc) => { toc.hidden = false; });
      this.classList.add('ara--has-toc');
      this.setActiveHeading(this.headings[0].id);
    }

    enhanceTables() {
      this.body.querySelectorAll('table').forEach((table, index) => {
        let wrapper = table.parentElement;
        const parentOverflow = wrapper ? this.ownerDocument.defaultView.getComputedStyle(wrapper).overflowX : '';
        const existingWrapper = wrapper && wrapper !== this.body && (
          wrapper.classList.contains('ara-table-scroll') ||
          wrapper.classList.contains('table-wrapper') ||
          parentOverflow === 'auto' || parentOverflow === 'scroll'
        );
        if (!existingWrapper) {
          wrapper = this.ownerDocument.createElement('div');
          table.before(wrapper);
          wrapper.append(table);
        }
        wrapper.classList.add('ara-table-scroll');
        if (!wrapper.hasAttribute('tabindex')) wrapper.tabIndex = 0;
        if (!wrapper.hasAttribute('role')) wrapper.setAttribute('role', 'region');
        if (!wrapper.hasAttribute('aria-label') && !wrapper.hasAttribute('aria-labelledby')) {
          const label = this.dataset.tableLabel;
          if (label) wrapper.setAttribute('aria-label', `${label} ${index + 1}`);
        }
      });
    }

    restoreInitialFragment() {
      const view = this.ownerDocument.defaultView;
      const hash = view.location.hash;
      let headingId;
      try {
        headingId = decodeURIComponent(hash.slice(1));
      } catch {
        return;
      }
      const heading = this.headings.find((item) => item.id === headingId);
      if (!heading) return;
      this.initialFragmentFrame = view.requestAnimationFrame(() => {
        this.initialFragmentFrame = null;
        if (!this.isConnected || view.location.hash !== hash) return;
        heading.scrollIntoView({
          block: 'start',
          behavior: view.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'auto'
        });
        this.setActiveHeading(heading.id);
      });
    }

    handleContentsClick(event) {
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const link = event.target.closest('[data-toc-link]');
      if (!link || !this.contains(link) || (link.target && link.target !== '_self')) return;
      const heading = this.headings.find((item) => item.id === link.dataset.headingId);
      if (!heading) return;
      if (!heading.hasAttribute('tabindex')) heading.tabIndex = -1;
      heading.focus({ preventScroll: true });
      this.setActiveHeading(heading.id);
      const view = this.ownerDocument.defaultView;
      if (this.contentsScrollFrame != null) view.cancelAnimationFrame(this.contentsScrollFrame);
      const hash = link.hash;
      this.contentsScrollFrame = view.requestAnimationFrame(() => {
        this.contentsScrollFrame = null;
        if (!this.isConnected || view.location.hash !== hash) return;
        heading.scrollIntoView({
          block: 'start',
          behavior: view.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'auto'
        });
        this.setActiveHeading(heading.id);
      });
    }

    setActiveHeading(id) {
      this.tocLinks.forEach((link) => {
        if (link.dataset.headingId === id) link.setAttribute('aria-current', 'location');
        else link.removeAttribute('aria-current');
      });
    }

    observeHeadings() {
      if (!('IntersectionObserver' in this.ownerDocument.defaultView)) return;
      this.visibleHeadings = new Set();
      this.observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) this.visibleHeadings.add(entry.target);
          else this.visibleHeadings.delete(entry.target);
        });
        const current = this.headings.find((heading) => this.visibleHeadings.has(heading));
        if (current) this.setActiveHeading(current.id);
      }, { rootMargin: '-100px 0px -60% 0px', threshold: 0 });
      this.headings.forEach((heading) => this.observer.observe(heading));
    }
  }

  customElements.define('aogeo-resource-article', AogeoResourceArticle);
}
